/**
 * The named placeholder every card surface shows without art (tokens without a printing, owner decision Q46) or
 * when the art failed to load (batch 3 #27): the card's name, cost, type and Might on the dark card gradient, so a
 * card stays readable and playable without its picture. On tiny cards (a phone's board) only the name and Might
 * remain (container query). `CardImage` adds "Retry artwork" where it is offered.
 */
import { cn } from './cn';

/** What the placeholder prints about the card. */
export interface CardPlaceholderFace {
  name: string;
  /** Energy cost, when the card has one. */
  cost?: number | null | undefined;
  /** "Unit", "Spell", "Champion Unit"… */
  type?: string | null | undefined;
  might?: number | null | undefined;
  token?: boolean | undefined;
}

/** A card face drawn without art. `large` is for the preview and the inspector (bigger type). */
export function CardPlaceholder({
  face,
  large,
  failed,
  className,
}: {
  face: CardPlaceholderFace;
  large?: boolean | undefined;
  /** The art failed (rather than not existing): says so under the name. */
  failed?: boolean | undefined;
  className?: string | undefined;
}) {
  const cost = face.cost ?? null;
  const might = face.might ?? null;
  return (
    <div
      className={cn(
        '@container absolute inset-0 flex flex-col items-center justify-center text-center rounded-[inherit] border border-[#293442]',
        'bg-[linear-gradient(160deg,#1f2130,#151622)]',
        large ? 'gap-2 p-4' : 'gap-1 p-1.5',
        className,
      )}
      data-testid="card-placeholder"
      data-failed={failed || undefined}
    >
      {cost !== null ? (
        <span
          className={cn(
            'absolute rounded-full bg-[#0c121a] text-text font-bold mono flex items-center justify-center shadow-[inset_0_0_0_1px_#3a4658]',
            large ? 'top-3 left-3 size-8 text-[15px]' : 'top-1 left-1 size-[18px] text-[10px] @max-[52px]:hidden',
          )}
          aria-label={`Cost ${cost}`}
        >
          {cost}
        </span>
      ) : null}
      <span
        className={cn(
          'leading-tight font-semibold text-text line-clamp-2 [overflow-wrap:anywhere]',
          large ? 'text-[18px]' : 'text-[10px] @max-[48px]:text-[8px]',
        )}
      >
        {face.name}
      </span>
      {face.type ? (
        <span className={cn('text-faint leading-tight', large ? 'text-[13px]' : 'text-[9px] @max-[48px]:hidden')}>
          {face.type}
        </span>
      ) : face.token ? (
        <span className={cn('text-faint', large ? 'text-[13px]' : 'text-[9px]')}>Token</span>
      ) : null}
      {might !== null ? (
        <span className={cn('mono font-bold text-[#a78bfa]', large ? 'text-[26px]' : 'text-[13px]')}>{might}</span>
      ) : null}
      {failed && large ? <span className="text-[12px] text-faint">Artwork didn’t load</span> : null}
    </div>
  );
}
